import { mutationProcedure } from '../trpc/common.js';
import type { ApiParams } from '../types/ApiParams.js';
import { notifyReload } from '../utilities/notifyReload.js';
import { ZodLinkPathInput, ZodLinkPathOutput, type LinkPathInput, type LinkPathOutput } from '../types/LinkPath.js';

/**
 * Unlinks a path to the session.
 */
export async function unlinkPath(
  params: ApiParams<
    LinkPathInput,
    'bus' | 'reporter' | 'telemetryClient',
    'incrementSessionVersion' | 'linkedPaths' | 'projectName' | 'removeLinkedPath' | 'resolveMap' | 'sequence'
  >,
): Promise<LinkPathOutput> {
  const { input, ctx } = params;
  const { linkedPath } = input;
  const { session, reporter, bus, telemetryClient } = ctx;
  const task = reporter.addTask(`Unlinking paths in ${session.projectName}`);
  let result = { resolveMap: session.resolveMap, linkedPaths: session.linkedPaths };
  try {
    await telemetryClient.tracer.startActiveSpan('UNLINK_PATH', async () => {
      // Remove linked paths from session
      result = await session.removeLinkedPath(linkedPath);
      task.complete({ status: 'complete', message: `Unlinked paths`, forceShow: true });
      session.incrementSessionVersion();
      await notifyReload({ reason: 'unlink-path' }, { bus, session, telemetryClient });
    });
  } catch (e) {
    task.complete({ status: 'fail', message: `Failed to unlink paths: ${(e as Error).stack || e}`, forceShow: true });
  }

  return result;
}

unlinkPath.procedure = mutationProcedure<LinkPathInput, LinkPathOutput>(
  unlinkPath,
  ZodLinkPathInput,
  ZodLinkPathOutput,
);
